import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Sidebar from './Sidebar';
import CartHeader from './CartHeader';
import GiftCardContainer from './GiftCardContainer';
import staticGiftCards from '../../constants/staticGiftCards';
import { addItemToCart, generateSubtotal } from '../../containers/Cart/cartSlice';
import determineCartCount from '../../helpers/cart/determineCartCount';
import HeroImage from '../../assets/hero-bg233.jpg';
import HeroImageSm from '../../assets/hero-bg-sm.png';
import GcCollageImage from '../../assets/gcards-collage.png';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

const CustomAccordion = styled(Accordion)(({ theme }) => ({
	'&:before': {
		display: 'none'
	},
	boxShadow: 'none',
	'&.Mui-expanded': {
		margin: 0
	}
}));
const CustomExpandMoreIcon = styled(ExpandMoreIcon)({
	fill: '#000'
});
const GiftCardDisplay = (props) => {
	const { addItemToCart, cartData, generateSubtotal, siteId, subtotal, userBalance } = props;

	const [currentPage, setCurrentPage] = React.useState(1);
	const [cardsPerPage, setCardsPerPage] = React.useState(24);
	const [filteredOptions, setFilteredOptions] = React.useState(staticGiftCards);
	const [currentCategory, setCurrentCategory] = React.useState('all');

	const handleFilterByInput = (inputValue) => {
		if (inputValue.length > 0) {
			return setFilteredOptions(
				staticGiftCards.filter((option) =>
					option?.company.toLowerCase().includes(inputValue.toLowerCase())
				)
			);
		} else {
			setFilteredOptions(staticGiftCards);
		}
	};

	const handleFilterByCategory = (filterValue) => {
		if (filterValue !== 'all') {
			setCurrentCategory(filterValue);
			return setFilteredOptions(
				staticGiftCards.filter((option) => option.category === filterValue)
			);
		} else {
			setCurrentCategory('all');
			return setFilteredOptions(staticGiftCards);
		}
	};

	const handlePageChange = (event, newPage) => {
		window.scrollTo({ top: 500, behavior: 'smooth' });
		setCurrentPage(newPage);
	};

	const paginateGiftCards = (giftCards, pageSize, currentPage) => {
		if (currentPage > giftCards.length / pageSize) {
			currentPage = Math.ceil(giftCards.length / pageSize);
		}

		return giftCards.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize);
	};

	const allGiftCards = paginateGiftCards(
		filteredOptions.sort((a, b) => b.popularity - a.popularity),
		cardsPerPage,
		currentPage
	);
	const [expanded, setExpanded] = useState(false);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};
	return (
		<Stack className='filter' sx={{ background: '#fff' }}>
			<Stack
				mb={{ xs: '25px', sm: '25px', md: '40px' }}
				mt={{ xs: 9, sm: 10, md: 13 }}
				mx='auto'
				direction='row'
				component='section'
				className='hero togglebg'
				sx={{
					position: 'relative',
					width: '100%',
					background: (theme) => theme.palette.heroBg.main,
					padding: {
						xs: '30px 0 25px',
						sm: '90px 0',
						md: '118px 0',
						lg: '138px 0',
						xl: '138px 0'
					}
				}}
			>
				<Stack
					sx={{
						textAlign: { xs: 'center', sm: 'left' },
						width: '100%',
						maxWidth: '1300px',
						position: 'relative',
						px: { xs: 4, sm: 5 }
					}}
					mx='auto'
				>
					<Typography
						variant='h1'
						sx={{
							fontSize: { xs: '1.6rem', sm: '1.9rem', md: '2.1rem', lg: '2.5rem' },
							fontWeight: '700',
							mb: 1,
							color: 'white',
							textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)'
						}}
					>
						GIFT CARDS
					</Typography>
					<Typography
						sx={{
							fontSize: { xs: '1.1rem', md: '1.2rem', lg: '1.6rem' },
							maxWidth: '649px',
							lineHeight: '130%',
							color: 'white',
							fontWeight: '500',
							paddingRight: { xs: 0, sm: '190px', md: '80px', lg: '0px' },
							textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)'
						}}
					>
						Save 20% on every e-Gift and physical gift card. Easy to redeem, enjoyable to spend.
					</Typography>
					<Box
						component='img'
						src={GcCollageImage}
						alt='Gift Cards'
						className='togglebg'
						sx={{
							position: { xs: 'relative', sm: 'absolute' },
							top: { xs: 'auto', sm: '-32px', md: '-62px' },
							right: { xs: 'auto', sm: '23px' },
							width: { xs: '179px', sm: '209px', md: '300px' },
							mt: { xs: 3, sm: 0 },
							mx: { xs: 'auto', sm: 0 }
						}}
					/>
				</Stack>
			</Stack>
			<Stack component='section' sx={{ width: '100%', maxWidth: '1307px', margin: '0 auto' }}>
				<Stack width='99%' sx={{ flexDirection: { sm: 'column', md: 'row' } }}>
					<Sidebar
						currentCategory={currentCategory}
						handleFilterByCategory={handleFilterByCategory}
						handleFilterByInput={handleFilterByInput}
					/>
					<Stack direction='row' flex={1}>
						<Divider orientation='vertical' flexItem />
						<Stack sx={{ minWidth: '100%', mb: '30px' }}>
							<CartHeader
								giftCards={filteredOptions}
								cardsPerPage={cardsPerPage}
								setCardsPerPage={setCardsPerPage}
								cartCount={determineCartCount(cartData)}
								currentCategory={currentCategory}
							/>
							<Grid container sx={{ px: { sm: '0px', md: '15px' } }}>
								{allGiftCards.map((giftCard, idx) => {
									return (
										<Grid
											item
											xs={12}
											sm={6}
											md={6}
											lg={4}
											xl={4}
											key={idx}
											sx={{
												flexBasis: '100%'
											}}
										>
											<GiftCardContainer
												giftCard={giftCard}
												addItemToCart={addItemToCart}
												generateSubtotal={generateSubtotal}
												siteId={siteId}
												subtotal={subtotal}
												userBalance={userBalance}
											/>
										</Grid>
									);
								})}
							</Grid>
							<Pagination
								size='large'
								variant='outlined'
								shape='rounded'
								hidePrevButton
								hideNextButton
								sx={{ alignSelf: 'center', my: 2, mr: { xs: 0, xl: 20 } }}
								count={Math.ceil(filteredOptions?.length / cardsPerPage)}
								onChange={handlePageChange}
								page={currentPage}
							/>
						</Stack>
					</Stack>
				</Stack>
			</Stack>
			<Stack
				alignItems='center'
				component='section'
				sx={{ py: { xs: 7, sm: 11 }, background: (theme) => theme.palette.common.lightWhite }}
			>
				<Typography
					variant='h4'
					sx={{
						mb: { xs: '25px', sm: '35px' },
						textAlign: 'center',
						fontSize: { xs: '1.5em', sm: '2.2em' }
					}}
					px={2}
				>
					Frequently asked Questions
				</Typography>
				<Stack sx={{ px: { xs: 1, sm: 3, md: 10, lg: 30, xl: 36 } }}>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel2'}
						onChange={handleChange('panel2')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel3a-content'
							id='panel3a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								Why can I only order up to {siteId === 282 ? '$60' : '$160'} in Cards per 30-day
								period?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography sx={{ pr: { lg: '0', xl: '130px' } }}>
								Our substantially discounted pricing on the most popular Gift Cards means we are
								only able to offer our customers up to $160 in Gift Cards per month. This policy
								means we are able to continuously offer these tremendous savings to the greatest
								number of customers each and every month.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel1'}
						onChange={handleChange('panel1')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel1a-content'
							id='panel1a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								How long will it take for my Physical Gift Cards to arrive?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								Your Physical Gift Cards will arrive 7-10 days after our receipt of your order.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel3'}
						onChange={handleChange('panel3')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel2a-content'
							id='panel2a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								What if I haven’t received my Gift Cards yet or I want to check the status of my
								Gift Cards?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography sx={{ pr: { lg: '0', xl: '130px' } }}>
								The first thing to do is check in the top-right of your screen or next to the ☰ on
								mobile for • by your Account. Click into your Account and you will see your order
								history and the status of your Gift Cards.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel4'}
						onChange={handleChange('panel4')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel4a-content'
							id='panel4a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								Can I track my Physical Gift Card orders?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography sx={{ pr: { lg: '0', xl: '130px' } }}>
								Absolutely — in fact, all Physical Gift Card orders come with free tracking.
								Whenever you submit an order, we will provide tracking information via an email to
								the email address associated with your account.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel5'}
						onChange={handleChange('panel5')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel5a-content'
							id='panel5a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								What payment options do you accept?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography sx={{ pr: { lg: '0', xl: '130px' } }}>
								We accept VISA, MasterCard, American Express, and Discover. All Gift Cards cannot be
								purchased with an international credit card.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel6'}
						onChange={handleChange('panel6')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel7a-content'
							id='panel7a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								Is my order secure?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography sx={{ pr: { lg: '0', xl: '130px' } }}>
								Yes. All personal and financial information is transmitted using the https protocol
								over a Secure Sockets Layer (SSL), utilizing state of the art technology for
								consumer protection. SSL is the industry standard and the best software available
								for secure transactions.
							</Typography>
						</AccordionDetails>
					</CustomAccordion>
					<CustomAccordion
						disableGutters
						sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
						expanded={expanded === 'panel7'}
						onChange={handleChange('panel7')}
					>
						<AccordionSummary
							expandIcon={<CustomExpandMoreIcon />}
							aria-controls='panel6a-content'
							id='panel6a-header'
						>
							<Typography sx={{ fontWeight: '500', fontSize: { md: '1em', xl: '1.2em' } }}>
								Does my Gift Card expire?
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>No, there is no expiration date on our Gift Cards.</Typography>
						</AccordionDetails>
					</CustomAccordion>
				</Stack>
			</Stack>
		</Stack>
	);
};

GiftCardDisplay.propTypes = {
	addItemToCart: PropTypes.func,
	cartData: PropTypes.array,
	generateSubtotal: PropTypes.func,
	siteId: PropTypes.number,
	subtotal: PropTypes.number,
	userBalance: PropTypes.object
};

const mapStateToProps = (state) => {
	return {
		cartData: state.cartSlice.cartData,
		siteId: state.authSlice.siteId,
		subtotal: state.cartSlice.subtotal,
		userBalance: state.authSlice.userBalance.data
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		addItemToCart: (payload) => {
			dispatch(addItemToCart(payload));
		},
		generateSubtotal: () => {
			dispatch(generateSubtotal());
		}
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(GiftCardDisplay);
